export enum ClientLoanDetailsLoanPartCollectionInterval {
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Halfyearly = 'Halfyearly',
  Yearly = 'Yearly',
}

export enum ClientDetailsBridgingStatus {
  Active = 'Active',
  Converted = 'Converted',
  Redeemed = 'Redeemed',
  Unknown = 'Unknown',
}

export interface ClientDetailsModel {
  loan: ClientDetailsLoan;
  loanParts: ClientDetailsLoanParts[];
  realEstates?: ClientDetailsRealEstate[];
  constructionDepot?: ClientDetailsConstructionDepot;
  sustainabilityDepot?: ClientDetailsSustainabilityDepot;
  persons?: ClientDetailsPerson[];
  collection?: ClientDetailsCollection;
  bridgings?: ClientDetailsBridging[];
  deeds?: ClientDetailsDeed[];
  tariffClass?: ClientDetailsTariffClass;
}

export interface ClientDetailsTariffClass {
  loanValue?: number;
  loanParts?: ClientDetailsTariffClassLoanParts[];
}

export interface ClientDetailsTariffClassLoanParts {
  number?: number;
  upperBound?: number;
  lowerBound?: number;
}

export interface ClientDetailsDeed {
  number?: number;
  type?: ClientDetailsDeedType;
  registrations?: ClientDetailsRegistration[];
}

export interface ClientDetailsRegistration {
  amount?: number;
  status?: ClientDetailsRegistrationStatus;
  openEnd?: boolean;
}

export enum ClientDetailsDeedType {
  Cooperation = 'Cooperation',
  CooperationPledging = 'CooperationPledging',
  Divorce = 'Divorce',
  DivorceMortgage = 'DivorceMortgage',
  DivorcePrivate = 'DivorcePrivate',
  Encompassing = 'Encompassing',
  External = 'External',
  Mortgage = 'Mortgage',
  Private = 'Private',
  ResidSociety = 'ResidSociety',
  ResidSocietyMember = 'ResidSocietyMember',
  Unknown = 'Unknown',
}

export enum ClientDetailsRegistrationStatus {
  Active = 'Active',
  Released = 'Released',
  Unknown = 'Unknown',
}

export interface ClientDetailsBridging {
  number?: number;
  principalSum?: number;
  residualDebt?: number;
  startDate?: string;
  endDate?: string;
  duration?: number;
  status?: ClientDetailsBridgingStatus;
}

export interface ClientDetailsLoan {
  number?: number;
  principalSum?: number;
  residualDebt?: number;
  conveyanceDate?: string;
  commercialProduct?: string;
}

export enum RealEstateRole {
  MainCollateral = 'MainCollateral',
  BridgingCollateral = 'BridgingCollateral',
  AdditionalCollateral = 'AdditionalCollateral',
}

export enum RealEstateEnergyEfficiencyReportMeasurementMethod {
  EI = 'EI',
  EPC = 'EPC',
  EL = 'EL',
}

export enum DeedType {
  Cooperation = 'Cooperation',
  CooperationPledging = 'CooperationPledging',
  Divorce = 'Divorce',
  DivorceMortgage = 'DivorceMortgage',
  DivorcePrivate = 'DivorcePrivate',
  Encompassing = 'Encompassing',
  External = 'External',
  Mortgage = 'Mortgage',
  Private = 'Private',
  ResidSociety = 'ResidSociety',
  ResidSocietyMember = 'ResidSocietyMember',
  Unknown = 'Unknown',
}

export enum RealEstateEnergyLabel {
  APlusPlusPlusPlus = 'APlusPlusPlusPlus',
  APlusPlusPlus = 'APlusPlusPlus',
  APlusPlus = 'APlusPlus',
  APlus = 'APlus',
  A = 'A',
  B = 'B',
  C = 'C',
  D = 'D',
  E = 'E',
  F = 'F',
  G = 'G',
  EnrgN = 'EnrgN',
  NulOld = 'NulOld',
  Unknown = 'Unknown',
  APlusPlusPlusPlusPlus = 'APlusPlusPlusPlusPlus',
}

export enum RealEstateRegistrationStatus {
  Active = 'Active',
  Released = 'Released',
  Unknown = 'Unknown',
}

export enum RealEstateValuationType {
  ValuationReportNwwi = 'ValuationReportNwwi',
  WozDecision = 'WozDecision',
  CalcasaDesktopStatement = 'CalcasaDesktopStatement',
}

export interface ClientDetailsLoanParts {
  product?: string;
  number?: number;
  duration?: number;
  principalSum?: number;
  residualDebt?: number;
  redemptionMethod?: ClientExtractLoanPartRedemptionMethod;
  startDate?: string;
  endDate?: string;
  date?: string;
  amount?: number;
  interestPeriod?: ClientDetailsInterestPeriod;
  guarantee: ClientExtractLoanPartGuarantee;
  collectionInterval?: ClientExtractLoanPartCollectionInterval;
}

export enum ClientExtractLoanPartCollectionInterval {
  Monthly = 'Monthly',
  Quarterly = 'Quarterly',
  Halfyearly = 'Halfyearly',
  Yearly = 'Yearly',
}

export enum ClientExtractLoanPartRedemptionMethod {
  Annuity = 'Annuity',
  Linear = 'Linear',
  AtOnce = 'AtOnce',
  Other = 'Other',
}

export enum ClientExtractLoanPartGuarantee {
  NHG = 'NHG',
  Communal = 'Communal',
  VAT = 'VAT',
  Na = 'Na',
  Unknown = 'Unknown',
}

export interface ClientDetailsInterestPeriod {
  duration?: number;
  startDate?: string;
  renewalDate?: string;
  effectiveInterestRate?: number;
  remainingDuration?: number;
}

export interface ClientDetailsConstructionDepot {
  type?: ConstructionDepotType;
  amount?: number;
  balance?: number;
  interestRate?: number;
  endDate?: string;
  startDate?: string;
}

export interface ClientDetailsSustainabilityDepot {
  amount?: number;
  balance?: number;
  interestRate?: number;
  startDate?: string;
}

export enum ConstructionDepotType {
  Existing = 'Existing',
  Rebuild = 'Rebuild',
  New = 'New',
  Unknown = 'Unknown',
}

export interface ClientDetailsRealEstate {
  type?: string;
  role?: RealEstateRole;
  location: ContactAddress;
  valuation: Valuation;
  constructionPlan?: RealEstateConstructionPlan;
  energyLabel?: RealEstateEnergyLabel;
}

export enum ClientDetailsRealEstateType {
  FamilyHouse = 'FamilyHouse',
  Utility = 'Utility',
  Rural = 'Rural',
  Grounds = 'Grounds',
  HouseWithGarage = 'HouseWithGarage',
  ApartmentWithGarage = 'ApartmentWithGarage',
  HouseAndGarage = 'HouseAndGarage',
  ApartmentAndGarage = 'ApartmentAndGarage',
  Conversion = 'Conversion',
  NRFResortion = 'NRFResortion',
  NRFRenovation = 'NRFRenovation',
  Apartment = 'Apartment',
  MobileHome = 'MobileHome',
  HouseBoat = 'HouseBoat',
  MveMgeHouse = 'MveMgeHouse',
  WoodenFrame = 'WoodenFrame',
  HomeAndIndustrial = 'HomeAndIndustrial',
  HouseAndShop = 'HouseAndShop',
  Shop = 'Shop',
  Recreational = 'Recreational',
  Farm = 'Farm',
  Garage = 'Garage',
  Office = 'Office',
  Industrial = 'Industrial',
  ApartmentWithParking = 'ApartmentWithParking',
  CooperativePropertyWithoutUmbrella = 'CooperativePropertyWithoutUmbrella',
  CooperativePropertyWithUmbrella = 'CooperativePropertyWithUmbrella',
  DetachedHouse = 'DetachedHouse',
  Hospitality = 'Hospitality',
  Hotel = 'Hotel',
  HouseAndHospitality = 'HouseAndHospitality',
  HouseAndOffice = 'HouseAndOffice',
  InvestmentProperty = 'InvestmentProperty',
  MortgageBackedSecurity = 'MortgageBackedSecurity',
  Other = 'Other',
  Parking = 'Parking',
  ProfitShareHouse = 'ProfitShareHouse',
  MVE_MGE = 'MVE_MGE',
  NRFCollateralRenovation = 'NRFCollateralRenovation',
  OfficeBuilding = 'OfficeBuilding',
  OtherAreas = 'OtherAreas',
  RecreationalOnPark = 'RecreationalOnPark',
  RuralProperties = 'RuralProperties',
  SingleGarageAtAppartment = 'SingleGarageAtAppartment',
  SingleGarageAtHome = 'SingleGarageAtHome',
  UtilityBuilding = 'UtilityBuilding',
}

export interface RealEstateConstructionPlan {
  name?: string;
  situationNumber?: string;
}

export interface RealEstateConversion {
  date: Date;
  marketValue: number;
}

export interface RealEstateDeed {
  conveyanceDate?: string;
  number?: string;
  type?: string;
}

export interface RealEstateRegistration {
  amount?: number;
  status?: RealEstateRegistrationStatus;
  openEnd?: boolean;
}

export interface Valuation {
  type?: RealEstateValuationType;
  date?: string;
  marketValue?: number;
  forcedSaleValue?: number;
}

export interface ClientDetailsPerson {
  type?: string;
  role?: ClientDetailsPersonRole;
  natural?: ClientDetailsPersonNatural;
  contact?: ClientDetailsPersonContact;
}

export enum ClientDetailsPersonRole {
  Debtor = 'Debtor',
  CoDebtor = 'CoDebtor',
}

export interface ClientDetailsCollection {
  startDate?: string;
  collectionAmount?: number;
  collectionAgreements: ClientDetailsCollectionAgreement[];
}

export interface ClientDetailsCollectionAgreement {
  ibanNumber?: string;
  paymentMethod?: string;
  percentage?: number;
}

interface ClientDetailsPersonNatural {
  name?: PersonNaturalName;
  dateOfBirth?: string;
  placeOfBirth?: string;
  gender?: string;
  nationality?: string;
  maritalStatus?: string;
  country?: string;
}

interface PersonNaturalName {
  initials?: string;
  lastNamePrefix?: string;
  firstName?: string;
  lastName?: string;
}

interface ClientDetailsPersonContact {
  address?: ContactAddress;
  emailAddress?: string;
  phoneNumber?: string;
}

interface ContactAddress {
  street?: string;
  houseNumber?: string;
  houseNumberSuffix?: string;
  postalCode?: string;
  city?: string;
  country?: Country;
}

interface Country {
  code?: string;
  displayName?: string;
}
